import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, MapPin, Clock, ExternalLink, Car, Bath, Dumbbell } from "lucide-react";
import { motion } from "framer-motion";
import { waNumber } from "@/components/PaymentMethodPicker";
import { LOGO_URL } from "@/components/Navbar";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Contact() {
  const { t } = useLanguage();
  const [club, setClub] = useState(null);
  useEffect(() => { api.get("/club").then(r => setClub(r.data)).catch(() => {}); }, []);

  const whatsapp = club?.whatsapp || "+230 57299188";
  const address = club?.address || "8 Ave de la Montagne, Morc Blackrock, Tamarin";
  const hours = (club?.opening_hours || "08:00-18:00").replace("-", " – ");
  const mapsUrl = club?.maps_url || `https://maps.google.com/?q=${encodeURIComponent(address + ", Mauritius")}`;
  const waUrl = `https://wa.me/${waNumber(whatsapp)}?text=${encodeURIComponent(t("contact.wa_hi"))}`;

  const cards = [
    { icon: MessageCircle, title: "WhatsApp", value: whatsapp, sub: t("contact.whatsapp_sub"), href: waUrl, cta: t("contact.whatsapp_cta"), color: "bg-[#25D366]", testId: "contact-whatsapp" },
    { icon: MapPin, title: t("contact.find_us"), value: address, sub: null, href: mapsUrl, cta: t("contact.maps_cta"), color: "bg-coral", external: true, testId: "contact-maps" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="contact-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-4 mb-10">
          <img src={club?.logo_url || LOGO_URL} alt="D&D Padel" className="w-16 h-16 rounded-2xl shadow-lg" />
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">{t("contact.title1")} <span className="text-golden">{t("contact.title2")}</span></h1>
            <p className="text-muted-foreground text-sm flex items-center gap-1.5 mt-1"><Clock size={14} className="text-teal" /> {t("contact.open")} <span className="text-teal font-semibold">{hours}</span></p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cards.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Card className="border-border/50 h-full card-lift" data-testid={c.testId}>
                <CardContent className="p-6 flex flex-col h-full">
                  <div className={`w-12 h-12 rounded-xl ${c.color} text-white flex items-center justify-center mb-4`}><c.icon size={22} /></div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{c.title}</p>
                  <p className="font-heading font-bold text-lg leading-snug mb-1">{c.value}</p>
                  {c.sub ? <p className="text-sm text-muted-foreground mb-5 flex-1">{c.sub}</p> : (
                    <ul className="text-sm text-muted-foreground mb-5 flex-1 space-y-1.5" data-testid="contact-amenities">
                      {[[Dumbbell, "contact.rental"], [Bath, "contact.restroom"], [Car, "contact.parking"]].map(([Icon, k]) => (
                        <li key={k} className="flex items-center gap-2" data-testid={`amenity-${k.split(".")[1]}`}><Icon size={14} className="text-teal shrink-0" /> {t(k)}</li>
                      ))}
                    </ul>
                  )}
                  <a href={c.href} target={c.external || c.title === "WhatsApp" ? "_blank" : undefined} rel="noreferrer">
                    <Button className={`w-full rounded-full text-white gap-2 ${c.color} hover:opacity-90`}>{c.cta}{c.external && <ExternalLink size={14} />}</Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 rounded-3xl border border-border/50 bg-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm" data-testid="contact-info">
          <div>
            <p className="font-heading font-bold text-base mb-2">{t("contact.payments")}</p>
            <p className="text-muted-foreground">{t("contact.payments_a")} <span className="font-mono font-semibold text-foreground">{club?.juice_number || "000050048236"}</span> {t("contact.payments_b")}</p>
          </div>
          <div>
            <p className="font-heading font-bold text-base mb-2">{t("contact.cancellations")}</p>
            <p className="text-muted-foreground">{t("contact.cancellations_a")} <Link to="/bookings" className="text-coral underline font-medium" data-testid="contact-my-bookings-link">{t("nav.bookings")}</Link>. {t("contact.cancellations_b")}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
